import Button, { ActionButton, BUTTON_VARIANT } from '@bugbug/core/components/Button';
import Input from '@bugbug/core/components/Input';
import { useFormik } from 'formik';
import { pick } from 'ramda';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import FormField from '~/components/FormField';
import { Header, Footer, Content } from '~/components/modals/Modal';
import ScreenSizeDropdown from '~/components/ScreenSizeDropdown';
import { SCREEN_RESOLUTION_TYPE } from '~/constants/test';
import useModal from '~/hooks/useModal';
import { useAppSelector } from '~/modules/store';
import { useUpdateTest } from '~/modules/tests/hooks/useUpdateTest/useUpdateTest';
import { selectCurrentTest } from '~/modules/tests/tests.selectors';
import { testNameValidator } from '~/utils/validators';

import { Container } from './TestSettingsModal.styled';

const FORM_FIELD = {
  NAME: 'name',
  SCREEN_SIZE: 'screenSizeType',
};

const TestSettingsSchema = Yup.object().shape({
  [FORM_FIELD.NAME]: testNameValidator.ensure(),
  [FORM_FIELD.SCREEN_SIZE]: Yup.string().oneOf(Object.values(SCREEN_RESOLUTION_TYPE)),
});

const TestSettingsModal = () => {
  const { t } = useTranslation();
  const modal = useModal();
  const test = useAppSelector(selectCurrentTest);
  const [updateTest, updateTestState] = useUpdateTest();

  const initialValues = useMemo(
    () => TestSettingsSchema.cast(pick(Object.values(FORM_FIELD), test)),
    [test],
  );

  const handleSubmit = useCallback(
    async (values) => {
      if (!test) return;

      const updatedTest = await updateTest(test.id, values);
      if (updatedTest) {
        modal.hide();
      }
    },
    // Unstable modal reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modal.hide, test?.id, updateTest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema: TestSettingsSchema,
    onSubmit: handleSubmit,
  });

  return (
    <Container data-testid="TestSettingsModal" onSubmit={formik.handleSubmit}>
      <Header>{t('testSettingsModal.title', 'Test settings')}</Header>
      <Content>
        <FormField label={t('testSettingsModal.name.label', 'Name')}>
          <Input
            {...formik.getFieldProps(FORM_FIELD.NAME)}
            data-hj-allow
            error={formik.touched.name && formik.errors.name}
            autoFocus
            fullWidth
          />
        </FormField>
        <FormField label={t('testSettingsModal.screenSizeType.label', 'Screen size')}>
          <ScreenSizeDropdown
            {...formik.getFieldProps(FORM_FIELD.SCREEN_SIZE)}
            onSettingsClick={modal.hide}
          />
        </FormField>
      </Content>
      <Footer>
        <Button onClick={modal.hide} disabled={updateTestState.isLoading}>
          {t('default.button.cancel')}
        </Button>
        <ActionButton
          variant={BUTTON_VARIANT.PRIMARY}
          type="submit"
          pending={updateTestState.isLoading}
        >
          {t('default.button.save')}
        </ActionButton>
      </Footer>
    </Container>
  );
};

export default TestSettingsModal;
